import React from 'react';
import { Link } from 'react-router-dom';
import routes from 'routes';

import styles from 'pages/admin/Meets/Meets.module.css';
import { convertDateTime } from 'utils/time';
import { ageGroupOptions } from 'config/admin/meet';
import { getUserPermissionLevel } from 'services/auth/tokenService';

import upcomingEventImage1 from 'assets/images/uifb.png';
import upcomingEventImage3 from 'assets/images/uijh.png';
import upcomingEventImage4 from 'assets/images/uiqw.png';
import upcomingEventImage2 from 'assets/images/uifd.png';
import TableViewButton from 'components/admin/TableViewButton';
import { UserPermissionLevel } from 'constants/auth';

export const ageOptionValuesToLabel = (ageOptionValues) => {
  if (!ageOptionValues) {
    return;
  }
  const values = ageOptionValues.split(',');

  const labels = [];

  values.forEach((value) => {
    const label = ageGroupOptions.find(
      (option) => option.value === value
    )?.label;
    if (label) {
      labels.push(label);
    }
  });

  return labels.join(', ');
};

/**
 * Gets age group labels from a Meet object.
 * Supports both the new JSON format and legacy integer string format.
 * @param {Object} meet - The meet object
 * @returns {string} Comma-separated age group names
 */
export const getMeetAgeGroupLabels = (meet) => {
  const ageGroups = meet?.ageGroups;

  // Already parsed JSON array: [{name: "U8", range: "U8"}, ...]
  if (Array.isArray(ageGroups) && ageGroups.length > 0) {
    return ageGroups.map((ag) => ag.name).join(', ');
  }

  if (typeof ageGroups === 'string' && ageGroups.length > 0) {
    // New JSON string format: "[{\"name\":\"U8\",...}]"
    if (ageGroups.startsWith('[')) {
      try {
        const parsed = JSON.parse(ageGroups);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed.map((ag) => ag.name).join(', ');
        }
      } catch (e) {
        // Fall through to legacy format
      }
    }

    // Legacy comma-separated integer string format: "0,1,2"
    return ageOptionValuesToLabel(ageGroups);
  }

  return '';
};

export const meetsToTableData = (meets, clubId) => {
  const results = [];
  const userPermissionLevel = getUserPermissionLevel();

  meets.forEach((meet, index) => {
    let image;
    switch (index % 4) {
      case 1:
        image = upcomingEventImage1;
        break;

      case 2:
        image = upcomingEventImage3;
        break;

      case 3:
        image = upcomingEventImage4;
        break;

      default:
        image = upcomingEventImage2;
    }
    const statusBadgeClass =
      {
        Draft: styles.badgeDraft,
        Upcoming: styles.badgeUpcoming,
        Complete: styles.badgeComplete,
      }[meet.meetStatus] || '';

    results.push({
      ...meet,
      date: `${convertDateTime(meet.meetDate).date} ${convertDateTime(meet.meetDate).time}`,
      image,
      age: getMeetAgeGroupLabels(meet),
      countsForPoints: (
        <span
          className={`${styles.badge} ${meet.countsForSeasonPoints ? styles.badgeYes : styles.badgeNo}`}
        >
          {meet.countsForSeasonPoints ? 'Yes' : 'No'}
        </span>
      ),
      countsForBests: (
        <span
          className={`${styles.badge} ${meet.countsForSeasonBests !== false ? styles.badgeYes : styles.badgeNo}`}
        >
          {meet.countsForSeasonBests !== false ? 'Yes' : 'No'}
        </span>
      ),
      statusBadge: (
        <span className={`${styles.badge} ${statusBadgeClass}`}>
          {meet.meetStatus}
        </span>
      ),
      buttons: (
        <div className={styles.buttons}>
          <TableViewButton
            url={routes.admin.showMeet.url(clubId, meet.meetID)}
            style={{ marginRight: 10 }}
          />
          {userPermissionLevel >= UserPermissionLevel.Official && (
            <Link
              to={routes.admin.editMeet.url(clubId, meet.meetID)}
              className={styles.editButton}
            >
              Edit
            </Link>
          )}
        </div>
      ),
    });
  });
  return results.sort((a, b) => new Date(b.meetDate) - new Date(a.meetDate));
};
